import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  PdfVector,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Voltik (Educational Connection Board)
 *
 * Layout: Template for a "Voltik" educational game board with holes for
 * connecting questions to answers. Two halves per page, each with question
 * grid on top and answer grid on bottom.
 *
 * Migrated from C# EduFrame.Single.Generators.Voltik
 */
export class Voltik implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 40;
  }

  private genMap(length: number, random: { next: (min: number, max: number) => number }): number[] {
    const map = Array.from({ length }, (_, i) => i);
    for (let i = map.length - 1; i > 0; i--) {
      const j = random.next(0, i + 1);
      [map[i], map[j]] = [map[j], map[i]];
    }
    return map;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const page = pdf.addPage();

    const count = Math.min(opts.list.length, this.countMax());

    const m1 = this.genMap(10, opts.data.random);
    const m2 = this.genMap(10, opts.data.random);
    const m3 = this.genMap(10, opts.data.random);
    const m4 = this.genMap(10, opts.data.random);

    let inst1Start: number;
    let inst2Start: number;
    let inst3Start: number;
    let inst4Start: number;

    if (count < 20) {
      inst1Start = 0;
      inst2Start = 0;
      inst3Start = 0;
      inst4Start = 0;
    } else if (count < 40) {
      inst1Start = 0;
      inst2Start = 0;
      inst3Start = 10;
      inst4Start = 10;
    } else {
      inst1Start = 0;
      inst2Start = 10;
      inst3Start = 20;
      inst4Start = 30;
    }

    this.generateHalf(page, opts, new PdfPoint(0, 0), inst1Start, inst2Start, m1, m2);
    this.generateHalf(
      page,
      opts,
      new PdfPoint(0, PdfSize.A4.height / 2),
      inst3Start,
      inst4Start,
      m3,
      m4
    );
  }

  private generateHalf(
    page: any,
    opts: OptsLabel,
    basePoint: PdfPoint,
    leftStartIndex: number,
    rightStartIndex: number,
    mLeft: number[],
    mRight: number[]
  ): void {
    const size = new PdfSize(PdfSize.A4.width, PdfSize.A4.height / 2);

    const question1Base = new PdfPoint(PdfUnit.FromMillimeters(43), PdfUnit.FromMillimeters(13));
    const question2Base = new PdfPoint(PdfUnit.FromMillimeters(151.5), PdfUnit.FromMillimeters(13));
    const questionSize = new PdfSize(PdfUnit.FromMillimeters(42), PdfUnit.FromMillimeters(15));

    const answer1Base = new PdfPoint(PdfUnit.FromMillimeters(17), PdfUnit.FromMillimeters(110));
    const answer2Base = new PdfPoint(PdfUnit.FromMillimeters(117), PdfUnit.FromMillimeters(110));
    const answerSize = new PdfSize(PdfUnit.FromMillimeters(19), PdfUnit.FromMillimeters(24));

    const ledPoint = new PdfPoint(PdfUnit.FromMillimeters(106), PdfUnit.FromMillimeters(21));

    page.drawRectangle(
      GUtil.cutPen,
      null,
      new PdfRect(basePoint.x, basePoint.y, size.width, size.height)
    );

    this.drawHole(page, ledPoint.move(new PdfVector(basePoint.x, basePoint.y)));

    const left = [8, 0, 6, 3, 5, 1, 4, 9, 7, 2];
    const right = [3, 0, 7, 4, 2, 8, 1, 6, 9, 5];

    this.generateGrid(
      page,
      opts,
      basePoint,
      question1Base,
      questionSize,
      answer1Base,
      answerSize,
      left,
      leftStartIndex,
      mLeft
    );
    this.generateGrid(
      page,
      opts,
      basePoint,
      question2Base,
      questionSize,
      answer2Base,
      answerSize,
      right,
      rightStartIndex,
      mRight
    );
  }

  private generateGrid(
    page: any,
    opts: OptsLabel,
    basePoint: PdfPoint,
    questionBase: PdfPoint,
    questionSize: PdfSize,
    answerBase: PdfPoint,
    answerSize: PdfSize,
    map: number[],
    startIndex: number,
    m: number[]
  ): void {
    const items = opts.list.slice(startIndex, startIndex + 10);
    if (items.length === 0) return;

    const d = PdfUnit.FromMillimeters(7);
    const d1 = PdfUnit.FromMillimeters(2);

    const questionWidth = questionSize.width - d * 2;

    const questionTexts = items.map((item) => [item.left.getText() ?? '']);
    const answerTexts = items.map((item) => [item.right.getText() ?? '']);

    const questionFont =
      questionTexts.length > 0
        ? page.adjustFontMulti(
            questionTexts,
            GUtil.baseFont,
            new PdfSize(questionWidth, questionSize.height)
          )
        : GUtil.baseFont;

    const answerFont =
      answerTexts.length > 0
        ? page.adjustFontMulti(
            answerTexts,
            GUtil.baseFont,
            new PdfSize(answerSize.width, answerSize.height - d * 1.5)
          )
        : GUtil.baseFont;

    // Draw questions (2x5 grid) - items are shuffled via m[index]
    for (let x = 0; x < 2; x++) {
      for (let y = 0; y < 5; y++) {
        const index = x * 5 + y;
        if (index >= items.length) continue;

        const shuffledIndex = m[index];

        const p = new PdfPoint(
          questionBase.x + x * questionSize.width + basePoint.x,
          questionBase.y + y * questionSize.height + basePoint.y
        );

        const r = new PdfRect(
          p.x - questionSize.width + d,
          p.y - questionSize.height / 2,
          questionSize.width,
          questionSize.height
        );

        const r1 = new PdfRect(r.x + d1, r.y, r.width - 2 * d, r.height);

        page.drawRectangle(null, GUtil.questionBrush, r);
        page.drawLabel(items[shuffledIndex].left, { rect: r1, font: questionFont });
        page.drawRectangle(GUtil.basePen, null, r);

        this.drawHole(page, p);
      }
    }

    // Draw answers (5x2 grid) - items are shuffled via m[map[index]]
    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 2; y++) {
        const index = y * 5 + x;
        if (index >= items.length) continue;

        const mappedIndex = m[map[index]];

        const p = new PdfPoint(
          answerBase.x + x * answerSize.width + basePoint.x,
          answerBase.y + y * answerSize.height + basePoint.y
        );

        const r = new PdfRect(
          p.x - answerSize.width / 2,
          p.y - answerSize.height + d,
          answerSize.width,
          answerSize.height
        );

        const r1 = new PdfRect(r.x, r.y, r.width, r.height - d * 1.5);

        page.drawRectangle(null, GUtil.answerBrush, r);
        if (mappedIndex < items.length) {
          page.drawLabel(items[mappedIndex].right, { rect: r1, font: answerFont });
        }
        page.drawRectangle(GUtil.basePen, null, r);

        this.drawHole(page, p);
      }
    }
  }

  private drawHole(page: any, p: PdfPoint): void {
    const holeSize = PdfUnit.FromMillimeters(7);
    const r = new PdfRect(p.x - holeSize / 2, p.y - holeSize / 2, holeSize, holeSize);
    page.drawEllipse(null, PdfBrush.White, r);
    page.drawEllipse(GUtil.basePen, null, r);
  }
}
